import React, { useEffect, useState } from 'react';
import { 
  Card, 
  message, 
  Button, 
  Space, 
  Row, 
  Col, 
  Table,
  Typography,
  Spin,
  Divider,
  Tag,
  Statistic
} from 'antd';
import { 
  ArrowLeftOutlined,
  PrinterOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Interfaces
interface TicketBalanza {
  id: number;
  numero_ticket: string;
  fecha_ticket: string | null;
  kilos_primera: number;
  kilos_segunda: number;
  kilos_netos: number;
  numero_remito: string;
  dtv: string;
  cliente_id: number;
  compra_servicio: string;
  cierre: string;
}

interface TicketDetallado {
  id: number;
  numero_ticket: string;
  cantidad: number;
  precio: number;
  total: number;
  ticket_balanza: TicketBalanza;
}

interface RetencionInfo {
  id: number;
  descripcion: string;
  tipo: string;
  porcentaje_base: number;
  portonelada_base: number;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  aplicar: boolean;
  provincia: string | null;
}

interface RetencionDetallada {
  id: number;
  portonelada: number;
  porcentaje: number;
  importe: number;
  total: number;
  retencion_info: RetencionInfo;
}

interface LiquidacionData {
  id: number;
  nro_compra: number;
  comprobante: string;
  fecha: string;
  cliente: number;
  cliente_nombre: string;
  cliente_nc: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  retenciones: number;
  alta: number;
  empresa: number;
  empresa_nombre: string;
  sucursal: number;
  sucursal_nombre: string;
}

interface Totales {
  total_kilos: number;
  total_subtotal: number;
  total_iva: number;
  total_retenciones: number;
  total_general: number;
  cantidad_tickets: number;
  cantidad_retenciones: number;
}

interface LiquidacionResponse {
  liquidacion: LiquidacionData;
  tickets: TicketDetallado[];
  retenciones: RetencionDetallada[];
  totales: Totales;
}

const EmisionCompraAlgodon: React.FC = () => {
  const [liquidacionData, setLiquidacionData] = useState<LiquidacionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (user?.id_empresa && user?.id_sucursal && id) {
      fetchLiquidacionData();
    }
  }, [user, id]);

  const fetchLiquidacionData = async () => {
    if (!user?.id_empresa || !user?.id_sucursal || !id) return;

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<LiquidacionResponse>(
        `${process.env.REACT_APP_LIQUIDACION_URL}/obtener_liquidacion_compraalgodon/${user.id_empresa}/${user.id_sucursal}/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLiquidacionData(response.data);
    } catch (error: any) {
      console.error('Error al cargar liquidación:', error);
      message.error('Error al cargar los datos de la liquidación');
      navigate('/liquidacion/listar_compraalgodon');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '$ 0,00';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(numValue);
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '0';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR').format(numValue);
  };

  const handleVolver = () => {
    navigate('/listar_compraalgodon');
  };

  const handleImprimir = () => {
    if (!liquidacionData) {
      message.warning('No hay datos para imprimir');
      return;
    }

    // Crear ventana de impresión
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('No se pudo abrir la ventana de impresión. Verifique que los pop-ups estén habilitados.');
      return;
    }

    const { liquidacion, tickets, retenciones, totales } = liquidacionData;

    // Generar HTML para impresión
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liquidación de Compra de Algodón - ${liquidacion.nro_compra}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #1890ff;
            padding-bottom: 15px;
          }
          .header h1 {
            color: #1890ff;
            margin: 0;
            font-size: 24px;
          }
          .info-section {
            margin-bottom: 25px;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .info-label {
            font-weight: bold;
            color: #1890ff;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 25px;
          }
          .stat-card {
            text-align: center;
            padding: 15px;
            border: 1px solid #d9d9d9;
            border-radius: 5px;
            background-color: #fff;
          }
          .stat-title {
            font-size: 14px;
            color: #666;
            margin-bottom: 5px;
          }
          .stat-value {
            font-size: 18px;
            font-weight: bold;
            color: #1890ff;
          }
          .stat-value.total {
            color: #3f8600;
            font-size: 20px;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #1890ff;
            margin: 25px 0 15px 0;
            border-bottom: 1px solid #e8e8e8;
            padding-bottom: 5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
          }
          th, td {
            border: 1px solid #d9d9d9;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
            color: #1890ff;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .summary-section {
            margin-top: 30px;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 5px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 16px;
          }
          .summary-label {
            font-weight: bold;
          }
          .summary-value {
            font-weight: bold;
          }
          .summary-total {
            border-top: 2px solid #3f8600;
            padding-top: 10px;
            margin-top: 15px;
            font-size: 20px;
            color: #3f8600;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #e8e8e8;
            padding-top: 15px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>LIQUIDACIÓN DE COMPRA DE ALGODÓN</h1>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">N° Compra:</span>
            <span>${liquidacion.nro_compra}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Comprobante:</span>
            <span>${liquidacion.comprobante || 'Sin asignar'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Fecha:</span>
            <span>${dayjs(liquidacion.fecha).format('DD/MM/YYYY')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Cliente:</span>
            <span>${liquidacion.cliente_nc} - ${liquidacion.cliente_nombre}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Empresa:</span>
            <span>${liquidacion.empresa_nombre}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Sucursal:</span>
            <span>${liquidacion.sucursal_nombre}</span>
          </div>
        </div>

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-title">Total Kilos</div>
            <div class="stat-value">${formatNumber(totales.total_kilos)} kg</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Subtotal</div>
            <div class="stat-value">${formatCurrency(totales.total_subtotal)}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">IVA</div>
            <div class="stat-value">${formatCurrency(totales.total_iva)}</div>
          </div>
          <div class="stat-card">
            <div class="stat-title">Total General</div>
            <div class="stat-value total">${formatCurrency(totales.total_general)}</div>
          </div>
        </div>

        <div class="section-title">Tickets de la Liquidación (${totales.cantidad_tickets})</div>
        <table>
          <thead>
            <tr>
              <th>N° Ticket</th>
              <th>Fecha</th>
              <th class="text-right">Kilos Netos</th>
              <th class="text-right">Precio/Tonelada</th>
              <th class="text-right">Total</th>
              <th>N° Remito</th>
              <th>DTV</th>
            </tr>
          </thead>
          <tbody>
            ${tickets.map(ticket => `
              <tr>
                <td>${ticket.numero_ticket}</td>
                <td>${ticket.ticket_balanza.fecha_ticket ? dayjs(ticket.ticket_balanza.fecha_ticket).format('DD/MM/YYYY') : '-'}</td>
                <td class="text-right">${formatNumber(ticket.cantidad)}</td>
                <td class="text-right">${formatCurrency(ticket.precio)}</td>
                <td class="text-right">${formatCurrency(ticket.total)}</td>
                <td>${ticket.ticket_balanza.numero_remito}</td>
                <td>${ticket.ticket_balanza.dtv}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="font-weight: bold; background-color: #f5f5f5;">
              <td colspan="2">Total</td>
              <td class="text-right">${formatNumber(totales.total_kilos)}</td>
              <td></td>
              <td class="text-right">${formatCurrency(totales.total_subtotal)}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>

        ${retenciones.length > 0 ? `
          <div class="section-title">Retenciones Aplicadas (${totales.cantidad_retenciones})</div>
          <table>
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Tipo</th>
                <th class="text-right">Portón/Tonelada</th>
                <th class="text-right">Porcentaje</th>
                <th class="text-right">Importe</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${retenciones.map(retencion => `
                <tr>
                  <td>${retencion.retencion_info.descripcion}</td>
                  <td>${retencion.retencion_info.tipo}</td>
                  <td class="text-right">${formatCurrency(retencion.portonelada)}</td>
                  <td class="text-right">${retencion.porcentaje}%</td>
                  <td class="text-right">${formatCurrency(retencion.importe)}</td>
                  <td class="text-right">${formatCurrency(retencion.total)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="font-weight: bold; background-color: #f5f5f5;">
                <td colspan="5">Total Retenciones</td>
                <td class="text-right">${formatCurrency(totales.total_retenciones)}</td>
              </tr>
            </tfoot>
          </table>
        ` : ''}

        <div class="summary-section">
          <div class="summary-row">
            <span class="summary-label">Subtotal:</span>
            <span class="summary-value">${formatCurrency(totales.total_subtotal)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">IVA:</span>
            <span class="summary-value">${formatCurrency(totales.total_iva)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Retenciones:</span>
            <span class="summary-value">-${formatCurrency(totales.total_retenciones)}</span>
          </div>
          <div class="summary-row summary-total">
            <span class="summary-label">TOTAL GENERAL:</span>
            <span class="summary-value">${formatCurrency(totales.total_general)}</span>
          </div>
        </div>

        <div class="footer">
          <p>Documento generado el ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
          <p>Servisoft - Sistema de Gestión de Algodón</p>
        </div>
      </body>
      </html>
    `;

    // Escribir el contenido y abrir impresión
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Esperar a que se cargue el contenido y abrir diálogo de impresión
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    message.success('Ventana de impresión abierta');
  };

  const handleExportar = () => {
    if (!liquidacionData) {
      message.warning('No hay datos para exportar');
      return;
    }

    try {
      const { liquidacion, tickets, retenciones, totales } = liquidacionData;

      // Crear ventana de impresión con estilos optimizados para PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        message.error('No se pudo abrir la ventana de exportación. Verifique que los pop-ups estén habilitados.');
        return;
      }

      // Generar HTML optimizado para PDF
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Liquidación de Compra de Algodón - ${liquidacion.nro_compra}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              color: #333;
              font-size: 12px;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #1890ff;
              padding-bottom: 10px;
            }
            .header h1 {
              color: #1890ff;
              margin: 0;
              font-size: 20px;
              font-weight: bold;
            }
            .info-section {
              margin-bottom: 20px;
              padding: 10px;
              background-color: #f8f9fa;
              border-radius: 3px;
              border: 1px solid #e9ecef;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            .info-label {
              font-weight: bold;
              color: #1890ff;
              min-width: 100px;
            }
            .info-value {
              flex: 1;
              text-align: right;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              margin-bottom: 20px;
            }
            .stat-card {
              text-align: center;
              padding: 10px;
              border: 1px solid #d9d9d9;
              border-radius: 3px;
              background-color: #fff;
            }
            .stat-title {
              font-size: 10px;
              color: #666;
              margin-bottom: 3px;
              font-weight: bold;
            }
            .stat-value {
              font-size: 14px;
              font-weight: bold;
              color: #1890ff;
            }
            .stat-value.total {
              color: #3f8600;
              font-size: 16px;
            }
            .section-title {
              font-size: 14px;
              font-weight: bold;
              color: #1890ff;
              margin: 20px 0 10px 0;
              border-bottom: 1px solid #e8e8e8;
              padding-bottom: 3px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 10px;
            }
            th, td {
              border: 1px solid #d9d9d9;
              padding: 4px;
              text-align: left;
              vertical-align: top;
            }
            th {
              background-color: #1890ff;
              color: white;
              font-weight: bold;
              text-align: center;
            }
            .text-right {
              text-align: right;
            }
            tfoot tr {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .summary-section {
              margin-top: 20px;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 3px;
              border: 1px solid #e9ecef;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 8px;
              font-size: 12px;
            }
            .summary-label {
              font-weight: bold;
            }
            .summary-value {
              font-weight: bold;
            }
            .summary-total {
              border-top: 2px solid #3f8600;
              padding-top: 8px;
              margin-top: 10px;
              font-size: 16px;
              color: #3f8600;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #e8e8e8;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>LIQUIDACIÓN DE COMPRA DE ALGODÓN</h1>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span class="info-label">N° Compra:</span>
              <span class="info-value">${liquidacion.nro_compra}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Comprobante:</span>
              <span class="info-value">${liquidacion.comprobante || 'Sin asignar'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Fecha:</span>
              <span class="info-value">${dayjs(liquidacion.fecha).format('DD/MM/YYYY')}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Cliente:</span>
              <span class="info-value">${liquidacion.cliente_nc} - ${liquidacion.cliente_nombre}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Empresa:</span>
              <span class="info-value">${liquidacion.empresa_nombre}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Sucursal:</span>
              <span class="info-value">${liquidacion.sucursal_nombre}</span>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-title">Total Kilos</div>
              <div class="stat-value">${formatNumber(totales.total_kilos)} kg</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">Subtotal</div>
              <div class="stat-value">${formatCurrency(totales.total_subtotal)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">IVA</div>
              <div class="stat-value">${formatCurrency(totales.total_iva)}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">Total General</div>
              <div class="stat-value total">${formatCurrency(totales.total_general)}</div>
            </div>
          </div>

          <div class="section-title">Tickets de la Liquidación (${totales.cantidad_tickets})</div>
          <table>
            <thead>
              <tr>
                <th>N° Ticket</th>
                <th>Fecha</th>
                <th class="text-right">Kilos Netos</th>
                <th class="text-right">Precio/Tonelada</th>
                <th class="text-right">Total</th>
                <th>N° Remito</th>
                <th>DTV</th>
              </tr>
            </thead>
            <tbody>
              ${tickets.map(ticket => `
                <tr>
                  <td>${ticket.numero_ticket}</td>
                  <td>${ticket.ticket_balanza.fecha_ticket ? dayjs(ticket.ticket_balanza.fecha_ticket).format('DD/MM/YYYY') : '-'}</td>
                  <td class="text-right">${formatNumber(ticket.cantidad)}</td>
                  <td class="text-right">${formatCurrency(ticket.precio)}</td>
                  <td class="text-right">${formatCurrency(ticket.total)}</td>
                  <td>${ticket.ticket_balanza.numero_remito}</td>
                  <td>${ticket.ticket_balanza.dtv}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2"><strong>Total</strong></td>
                <td class="text-right"><strong>${formatNumber(totales.total_kilos)}</strong></td>
                <td></td>
                <td class="text-right"><strong>${formatCurrency(totales.total_subtotal)}</strong></td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>

          ${retenciones.length > 0 ? `
            <div class="section-title">Retenciones Aplicadas (${totales.cantidad_retenciones})</div>
            <table>
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th>Tipo</th>
                  <th class="text-right">Portón/Tonelada</th>
                  <th class="text-right">Porcentaje</th>
                  <th class="text-right">Importe</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${retenciones.map(retencion => `
                  <tr>
                    <td>${retencion.retencion_info.descripcion}</td>
                    <td>${retencion.retencion_info.tipo}</td>
                    <td class="text-right">${formatCurrency(retencion.portonelada)}</td>
                    <td class="text-right">${retencion.porcentaje}%</td>
                    <td class="text-right">${formatCurrency(retencion.importe)}</td>
                    <td class="text-right">${formatCurrency(retencion.total)}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5"><strong>Total Retenciones</strong></td>
                  <td class="text-right"><strong>${formatCurrency(totales.total_retenciones)}</strong></td>
                </tr>
              </tfoot>
            </table>
          ` : ''}

          <div class="summary-section">
            <div class="summary-row">
              <span class="summary-label">Subtotal:</span>
              <span class="summary-value">${formatCurrency(totales.total_subtotal)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">IVA:</span>
              <span class="summary-value">${formatCurrency(totales.total_iva)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Retenciones:</span>
              <span class="summary-value">-${formatCurrency(totales.total_retenciones)}</span>
            </div>
            <div class="summary-row summary-total">
              <span class="summary-label">TOTAL GENERAL:</span>
              <span class="summary-value">${formatCurrency(totales.total_general)}</span>
            </div>
          </div>

          <div class="footer">
            <p>Documento generado el ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
            <p>Servisoft - Sistema de Gestión de Algodón</p>
          </div>
        </body>
        </html>
      `;

      // Escribir el contenido
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Esperar a que se cargue y abrir diálogo de impresión/guardar como PDF
      printWindow.onload = () => {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };

      message.success('Ventana de exportación abierta. Use "Guardar como PDF" en el diálogo de impresión.');
      
    } catch (error) {
      console.error('Error al exportar:', error);
      message.error('Error al exportar el documento');
    }
  };

  // Columnas para la tabla de tickets
  const columnsTickets: ColumnsType<TicketDetallado> = [
    {
      title: 'N° Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 120,
    },
    {
      title: 'Fecha',
      dataIndex: ['ticket_balanza', 'fecha_ticket'],
      key: 'fecha_ticket',
      width: 110,
      render: (value) => value ? dayjs(value).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Kilos Netos',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 120,
      align: 'right',
      render: (value) => formatNumber(value),
    },
    {
      title: 'Precio/Tonelada',
      dataIndex: 'precio',
      key: 'precio',
      width: 140,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'N° Remito',
      dataIndex: ['ticket_balanza', 'numero_remito'],
      key: 'numero_remito',
      width: 120,
    },
    {
      title: 'DTV',
      dataIndex: ['ticket_balanza', 'dtv'],
      key: 'dtv',
      width: 100,
    },
  ];

  // Columnas para la tabla de retenciones
  const columnsRetenciones: ColumnsType<RetencionDetallada> = [
    {
      title: 'Descripción',
      dataIndex: ['retencion_info', 'descripcion'],
      key: 'descripcion',
      width: 200,
    },
    {
      title: 'Tipo',
      dataIndex: ['retencion_info', 'tipo'],
      key: 'tipo',
      width: 100,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: 'Portón/Tonelada',
      dataIndex: 'portonelada',
      key: 'portonelada',
      width: 140,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Porcentaje',
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      width: 100,
      align: 'right',
      render: (value) => `${value}%`,
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!liquidacionData) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="danger">No se pudieron cargar los datos de la liquidación</Text>
          </div>
        </Card>
      </div>
    );
  }

  const { liquidacion, tickets, retenciones, totales } = liquidacionData;

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <Title 
            level={3} 
            style={{ 
              margin: 0,
              backgroundColor: '#1890ff',
              color: 'white',
              textAlign: 'center',
              padding: '12px 24px',
              borderRadius: '6px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 'bold'
            }}
          >
            Emisión de Liquidación de Compra de Algodón
          </Title>
          
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleVolver}
            >
              Volver
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handleImprimir}
            >
              Imprimir
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportar}
            >
              Exportar
            </Button>
          </Space>
        </div>

        {/* Información de la liquidación */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Text strong>N° Compra:</Text>
              <br />
              <Text>{liquidacion.nro_compra}</Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Comprobante:</Text>
              <br />
              <Text>{liquidacion.comprobante || 'Sin asignar'}</Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Fecha:</Text>
              <br />
              <Text>{dayjs(liquidacion.fecha).format('DD/MM/YYYY')}</Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Cliente:</Text>
              <br />
              <Text>{liquidacion.cliente_nc} - {liquidacion.cliente_nombre}</Text>
            </Col>
          </Row>
        </Card>

        {/* Estadísticas */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Kilos"
                value={totales.total_kilos}
                formatter={(value) => formatNumber(value)}
                suffix="kg"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Subtotal"
                value={totales.total_subtotal}
                formatter={(value) => formatCurrency(value)}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="IVA"
                value={totales.total_iva}
                formatter={(value) => formatCurrency(value)}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total General"
                value={totales.total_general}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Tabla de Tickets */}
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <FileTextOutlined style={{ marginRight: '8px' }} />
                Tickets de la Liquidación ({totales.cantidad_tickets})
              </span>
            </div>
          }
          style={{ marginBottom: '16px' }}
        >
          <Table
            columns={columnsTickets}
            dataSource={tickets}
            rowKey="id"
            pagination={false}
            scroll={{ x: 800 }}
            size="small"
            bordered
            locale={{
              emptyText: 'No hay tickets registrados'
            }}
            summary={(pageData) => {
              const totalKilos = pageData.reduce((sum, item) => sum + item.cantidad, 0);
              const totalImporte = pageData.reduce((sum, item) => sum + item.total, 0);
              
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{formatNumber(totalKilos)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} align="right">
                      <Text strong>{formatCurrency(totalImporte)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} colSpan={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>

        {/* Tabla de Retenciones */}
        {retenciones.length > 0 && (
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <FileTextOutlined style={{ marginRight: '8px' }} />
                  Retenciones Aplicadas ({totales.cantidad_retenciones})
                </span>
              </div>
            }
            style={{ marginBottom: '16px' }}
          >
            <Table
              columns={columnsRetenciones}
              dataSource={retenciones}
              rowKey="id"
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
              bordered
              locale={{
                emptyText: 'No hay retenciones aplicadas'
              }}
              summary={(pageData) => {
                const totalRetenciones = pageData.reduce((sum, item) => sum + item.total, 0);
                
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        <Text strong>Total Retenciones</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} />
                      <Table.Summary.Cell index={5} align="right">
                        <Text strong>{formatCurrency(totalRetenciones)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        )}

        {/* Resumen Final */}
        <Card title="Resumen de Liquidación" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  Subtotal
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  IVA
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                  Retenciones
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '16px', color: '#3f8600' }}>
                  Total General
                </Text>
              </div>
            </Col>
          </Row>
          
          <Divider style={{ margin: '8px 0', borderTop: '0.5px solid #e8e8e8' }} />
          
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                  {formatCurrency(totales.total_subtotal)}
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                  {formatCurrency(totales.total_iva)}
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '18px', color: '#ff4d4f' }}>
                  -{formatCurrency(totales.total_retenciones)}
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '20px', color: '#3f8600' }}>
                  {formatCurrency(totales.total_general)}
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      </Card>
    </div>
  );
};

export default EmisionCompraAlgodon;
